import React, { useEffect, useState, useRef } from 'react';
import { PlayIcon, PauseIcon } from 'lucide-react';
interface AudioPlayerProps {
  src: string;
  title: string;
}
export function AudioPlayer({
  src,
  title
}: AudioPlayerProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => setDuration(audio.duration);
    const handleEnded = () => setIsPlaying(false);
    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('ended', handleEnded);
    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('ended', handleEnded);
    };
  }, []);
  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };
  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current;
    if (!audio) return;
    const newTime = parseFloat(e.target.value);
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };
  const formatTime = (time: number) => {
    if (isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };
  const progress = duration > 0 ? currentTime / duration * 100 : 0;
  return <div className="bg-dark-lighter border border-teal/30 rounded-lg p-6 space-y-4" data-id="element-12">
      <audio ref={audioRef} src={src} preload="metadata" data-id="element-13" />

      <div className="flex items-center gap-4" data-id="element-14">
        <button onClick={togglePlay} className="w-12 h-12 flex items-center justify-center bg-teal hover:bg-teal/90 rounded-full transition-colors flex-shrink-0" aria-label={isPlaying ? 'Pause' : 'Play'} data-id="element-15">
          {isPlaying ? <PauseIcon className="w-6 h-6 text-dark" data-id="element-16" /> : <PlayIcon className="w-6 h-6 text-dark ml-0.5" data-id="element-17" />}
        </button>

        <div className="flex-1 space-y-2" data-id="element-18">
          <p className="font-inter text-sm font-semibold text-white" data-id="element-19">{title}</p>

          <div className="relative" data-id="element-20">
            <input type="range" min="0" max={duration || 0} value={currentTime} onChange={handleSeek} className="w-full h-2 bg-dark rounded-lg appearance-none cursor-pointer audio-progress" style={{
            background: `linear-gradient(to right, #00D4AA 0%, #00D4AA ${progress}%, #0A0A0F ${progress}%, #0A0A0F 100%)`
          }} data-id="element-21" />
          </div>

          <div className="flex justify-between text-xs text-text/60 font-inter" data-id="element-22">
            <span data-id="element-23">{formatTime(currentTime)}</span>
            <span data-id="element-24">{formatTime(duration)}</span>
          </div>
        </div>
      </div>
    </div>;
}